import type { StudyMissionSelectDto } from '@@types/api.types';
import { averageScores } from '@azymmetric/shared/types/meia/scoring.types';
import type { MeiaMissionMeasureFlags } from '@queries/meia.query';
import type { MeiaPhaseKey } from '../meia.constants';

/**
 * Client-side phase progress, following the v4.5.2 prototype formulas
 * (index-v4.5.2.html L11638-11695):
 * - P1 — fraction of the 10 mission-framing fields populated.
 * - P2 — per thread, % stages with >=1 task; averaged across the mission's threads.
 * - P3 — all-stages-across-all-threads task coverage in the mission.
 * - P4 — mean thread verdict score across the mission's scored threads.
 * - P5 — 100 when the study has an active G4 sign-off, else 0 (the prototype
 *   hardcodes 0; AZ has a real sign-off to reflect).
 *
 * The prototype's P1 counts free-text mos/moe/mop fields. AZ models measures
 * structurally, so those three checks read the mission-scoped MoS/MoE/MoP
 * entries instead. For P4 the prototype filters `score > 0` because 0 is its
 * "unscored" sentinel; AZ verdicts can legitimately score 0 (RED), so unscored
 * threads are excluded by map membership instead.
 */

export type MeiaProgressStage = {
  id: string;
  threadId: string;
};

export type MeiaProgressTaskRow = {
  swimlaneId: string;
  threadId: string;
};

export type MeiaProgressThread = {
  id: string;
  missionId: string;
};

export type MeiaMissionPhaseProgressParams = {
  isStudySignedOff: boolean;
  measureFlagsByMissionId: Map<string, MeiaMissionMeasureFlags>;
  mission: StudyMissionSelectDto;
  stages: MeiaProgressStage[];
  taskRows: MeiaProgressTaskRow[];
  threadScoresByThreadId: Map<string, number>;
  threads: MeiaProgressThread[];
};

export type MeiaPhaseProgressBand = 'empty' | 'green' | 'orange' | 'yellow';

const P1_FRAMING_FIELD_COUNT = 10;

const isFilled = (value: string | null | undefined): boolean => Boolean(value?.trim());

const getMissionFramingProgress = (params: MeiaMissionPhaseProgressParams): number => {
  const { measureFlagsByMissionId, mission } = params;
  const context = mission.missionContext;
  const measureFlags = measureFlagsByMissionId.get(mission.id);

  const checks = [
    isFilled(context?.desiredMissionOutcome),
    isFilled(context?.commandersIntent),
    isFilled(context?.areaOfOperations),
    isFilled(context?.timeWindow),
    isFilled(context?.blueForceComposition),
    isFilled(context?.authoritiesRoeEnvelope),
    isFilled(context?.assumptionsConstraints),
    Boolean(measureFlags?.hasMos),
    Boolean(measureFlags?.hasMoe),
    Boolean(measureFlags?.hasMop),
  ];

  const filled = checks.filter(Boolean).length;
  return Math.round((filled / P1_FRAMING_FIELD_COUNT) * 100);
};

const getMissionStageCoverage = (params: MeiaMissionPhaseProgressParams): { covered: number; total: number } => {
  const { mission, stages, taskRows, threads } = params;
  const missionThreadIds = new Set(
    threads.filter((thread) => thread.missionId === mission.id).map((thread) => thread.id),
  );
  const coveredStageIds = new Set(taskRows.map((row) => row.swimlaneId));
  const missionStages = stages.filter((stage) => missionThreadIds.has(stage.threadId));

  return {
    covered: missionStages.filter((stage) => coveredStageIds.has(stage.id)).length,
    total: missionStages.length,
  };
};

const getThreadDesignProgress = (params: MeiaMissionPhaseProgressParams): number => {
  const { mission, stages, taskRows, threads } = params;
  const missionThreads = threads.filter((thread) => thread.missionId === mission.id);
  if (missionThreads.length === 0) return 0;

  const coveredStageIds = new Set(taskRows.map((row) => row.swimlaneId));
  let accumulated = 0;
  let threadCount = 0;

  for (const thread of missionThreads) {
    const threadStages = stages.filter((stage) => stage.threadId === thread.id);
    if (threadStages.length === 0) continue;
    const covered = threadStages.filter((stage) => coveredStageIds.has(stage.id)).length;
    accumulated += covered / threadStages.length;
    threadCount += 1;
  }

  return threadCount === 0 ? 0 : Math.round((accumulated / threadCount) * 100);
};

const getTaskCanvasProgress = (params: MeiaMissionPhaseProgressParams): number => {
  const { covered, total } = getMissionStageCoverage(params);
  return total === 0 ? 0 : Math.round((covered / total) * 100);
};

const getScoringProgress = (params: MeiaMissionPhaseProgressParams): number => {
  const { mission, threadScoresByThreadId, threads } = params;
  const scores = threads
    .filter((thread) => thread.missionId === mission.id && threadScoresByThreadId.has(thread.id))
    .map((thread) => threadScoresByThreadId.get(thread.id) ?? 0);

  return Math.round(averageScores(scores) ?? 0);
};

export const getMeiaMissionPhaseProgress = (phase: MeiaPhaseKey, params: MeiaMissionPhaseProgressParams): number => {
  switch (phase) {
    case 'p1':
      return getMissionFramingProgress(params);
    case 'p2':
      return getThreadDesignProgress(params);
    case 'p3':
      return getTaskCanvasProgress(params);
    case 'p4':
      return getScoringProgress(params);
    case 'p5':
      return params.isStudySignedOff ? 100 : 0;
  }
};

export const getMeiaStudyPhaseProgress = (
  phase: MeiaPhaseKey,
  params: Omit<MeiaMissionPhaseProgressParams, 'mission'> & { missions: StudyMissionSelectDto[] },
): number => {
  const { missions, ...rest } = params;
  if (missions.length === 0) return 0;

  const values = missions.map((mission) => getMeiaMissionPhaseProgress(phase, { ...rest, mission }));
  return Math.round(values.reduce((acc, value) => acc + value, 0) / values.length);
};

export const getMeiaPhaseProgressBand = (percentage: number): MeiaPhaseProgressBand => {
  if (percentage >= 80) return 'green';
  if (percentage >= 50) return 'yellow';
  if (percentage > 0) return 'orange';
  return 'empty';
};
